import React, { useState } from 'react';
import { SalaryInputs, PayFrequency, TaxYear, UKRegion, PensionMethod, StudentLoanPlan, NICategory } from '../types/calculator';
import { parseTaxCode } from '../utils/taxCalculator';
import { Sliders, ChevronDown, ChevronUp, Info, HelpCircle, Shield, Briefcase, GraduationCap, Percent, PoundSterling } from 'lucide-react';

interface SalaryInputFormProps {
  inputs: SalaryInputs;
  onChange: (updated: SalaryInputs) => void;
}

export const SalaryInputForm: React.FC<SalaryInputFormProps> = ({ inputs, onChange }) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleChange = <K extends keyof SalaryInputs>(key: K, value: SalaryInputs[K]) => {
    onChange({
      ...inputs,
      [key]: value,
    });
  };

  const parsedTaxCodeInfo = parseTaxCode(inputs.taxCode, inputs.isBlind, inputs.marriageAllowance);

  return (
    <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-5 sm:p-6 space-y-6 shadow-2xl text-zinc-100">
      <div className="flex items-center justify-between border-b border-zinc-800 pb-4">
        <div>
          <h2 className="text-lg font-extrabold text-white flex items-center gap-2">
            <PoundSterling className="w-5 h-5 text-emerald-400" />
            Enter Salary &amp; Work Details
          </h2>
          <p className="text-xs text-zinc-400 mt-0.5">
            Instant net salary calculation under current UK tax laws
          </p>
        </div>
        <span className="text-xs bg-zinc-950 text-emerald-400 border border-zinc-800 px-2.5 py-1 rounded-full font-bold">
          Tax Year {inputs.taxYear}
        </span>
      </div>

      {/* Main Income Input Grid */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        {/* Salary Amount Input */}
        <div className="md:col-span-7">
          <label htmlFor="grossIncome" className="block text-xs font-bold uppercase tracking-wider text-zinc-300 mb-1.5">
            Gross Salary Amount
          </label>
          <div className="relative rounded-xl">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-zinc-400 font-bold text-lg font-mono">
              £
            </div>
            <input
              id="grossIncome"
              type="number"
              min="0"
              step="100"
              value={inputs.grossIncome === 0 ? '' : inputs.grossIncome}
              onChange={(e) => handleChange('grossIncome', parseFloat(e.target.value) || 0)}
              placeholder="e.g. 35000"
              className="block w-full rounded-xl border border-zinc-800 bg-zinc-950 pl-9 pr-4 py-3 text-white font-mono font-bold text-xl placeholder:text-zinc-600 focus:border-emerald-500 focus:outline-none transition-all"
            />
          </div>
        </div>

        {/* Pay Frequency Dropdown */}
        <div className="md:col-span-5">
          <label htmlFor="incomePeriod" className="block text-xs font-bold uppercase tracking-wider text-zinc-300 mb-1.5">
            Pay Frequency
          </label>
          <select
            id="incomePeriod"
            value={inputs.incomePeriod}
            onChange={(e) => handleChange('incomePeriod', e.target.value as PayFrequency)}
            className="block w-full rounded-xl border border-zinc-800 bg-zinc-950 px-3.5 py-3.5 text-white font-semibold text-sm focus:border-emerald-500 focus:outline-none transition-all"
          >
            <option value="year">Per Year (Annual)</option>
            <option value="month">Per Month</option>
            <option value="4week">Every 4 Weeks</option>
            <option value="2week">Every 2 Weeks (Fortnightly)</option>
            <option value="week">Per Week</option>
            <option value="day">Per Day</option>
            <option value="hour">Per Hour</option>
          </select>
        </div>
      </div>

      {/* Quick Salary Amount Slider */}
      <div className="space-y-1 pt-1">
        <div className="flex justify-between text-xs text-zinc-400 font-mono">
          <span>£10,000</span>
          <span>£50,000</span>
          <span>£100,000</span>
          <span>£150,000+</span>
        </div>
        <input
          type="range"
          aria-label="Quick-adjust gross salary"
          min="10000"
          max="200000"
          step="1000"
          value={inputs.incomePeriod === 'year' ? Math.min(200000, Math.max(10000, inputs.grossIncome)) : 35000}
          disabled={inputs.incomePeriod !== 'year'}
          onChange={(e) => handleChange('grossIncome', parseFloat(e.target.value))}
          className="w-full h-2 bg-zinc-950 border border-zinc-800 rounded-lg appearance-none cursor-pointer accent-emerald-500 disabled:opacity-40"
        />
      </div>

      {/* Hours and Days per week (if hourly/daily calculation) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-zinc-950/80 p-3.5 rounded-xl border border-zinc-800/80 text-xs">
        <div>
          <label htmlFor="hoursPerWeek" className="block text-zinc-400 font-medium mb-1">Hours / Week</label>
          <input
            id="hoursPerWeek"
            type="number"
            min="1"
            max="168"
            value={inputs.hoursPerWeek}
            onChange={(e) => handleChange('hoursPerWeek', parseFloat(e.target.value) || 37.5)}
            className="w-full rounded-lg border border-zinc-800 px-2.5 py-1.5 text-white font-mono font-semibold bg-zinc-900 focus:border-emerald-500 focus:outline-none"
          />
        </div>
        <div>
          <label htmlFor="daysPerWeek" className="block text-zinc-400 font-medium mb-1">Days / Week</label>
          <input
            id="daysPerWeek"
            type="number"
            min="1"
            max="7"
            value={inputs.daysPerWeek}
            onChange={(e) => handleChange('daysPerWeek', parseFloat(e.target.value) || 5)}
            className="w-full rounded-lg border border-zinc-800 px-2.5 py-1.5 text-white font-mono font-semibold bg-zinc-900 focus:border-emerald-500 focus:outline-none"
          />
        </div>
        <div>
          <label htmlFor="region" className="block text-zinc-400 font-medium mb-1">UK Region</label>
          <select
            id="region"
            value={inputs.region}
            onChange={(e) => handleChange('region', e.target.value as UKRegion)}
            className="w-full rounded-lg border border-zinc-800 px-2 py-1.5 text-white font-semibold bg-zinc-900 focus:border-emerald-500 focus:outline-none"
          >
            <option value="england_ni_wales">England, NI &amp; Wales</option>
            <option value="scotland">Scotland (Scottish Tax)</option>
          </select>
        </div>
        <div>
          <label htmlFor="taxCode" className="block text-zinc-400 font-medium mb-1">Tax Code</label>
          <input
            id="taxCode"
            type="text"
            value={inputs.taxCode}
            onChange={(e) => handleChange('taxCode', e.target.value.toUpperCase())}
            placeholder="1257L"
            className="w-full rounded-lg border border-zinc-800 px-2.5 py-1.5 text-emerald-400 font-bold bg-zinc-900 font-mono uppercase focus:border-emerald-500 focus:outline-none"
          />
        </div>
      </div>

      {/* Tax Code Helper Badge */}
      <div className="flex items-center gap-2 text-xs text-zinc-300 bg-emerald-950/30 border border-emerald-500/30 p-3 rounded-xl">
        <Info className="w-4 h-4 text-emerald-400 shrink-0" />
        <div>
          <span className="font-bold text-white">Tax Code ({inputs.taxCode}):</span>{' '}
          {parsedTaxCodeInfo.isSpecialCode ? (
            <span className="text-emerald-300">Special Code ({parsedTaxCodeInfo.specialType})</span>
          ) : (
            <span>
              Tax-free personal allowance of{' '}
              <strong className="text-emerald-400 font-mono font-bold">
                £{parsedTaxCodeInfo.personalAllowance.toLocaleString()}
              </strong>
              {parsedTaxCodeInfo.isScottish ? ' (Scottish Tax Bands)' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Primary Pension & Student Loan Settings */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
        {/* Pension Contribution */}
        <div className="border border-zinc-800 rounded-xl p-3.5 space-y-2 bg-zinc-950/60">
          <div className="space-y-1.5">
            <label htmlFor="pensionMethod" className="text-xs font-bold text-white flex items-center gap-1.5">
              <Briefcase className="w-3.5 h-3.5 text-emerald-400" />
              Workplace Pension
            </label>
            <select
              id="pensionMethod"
              value={inputs.pensionMethod}
              onChange={(e) => handleChange('pensionMethod', e.target.value as PensionMethod)}
              title={inputs.pensionMethod === 'salary_sacrifice' ? 'Salary Sacrifice (Saves Tax & NI)' : undefined}
              className="w-full text-[11px] border border-zinc-800 rounded px-1.5 py-1 bg-zinc-900 text-zinc-300 font-medium focus:border-emerald-500 focus:outline-none truncate"
            >
              <option value="salary_sacrifice">Salary Sacrifice (Saves Tax &amp; NI)</option>
              <option value="net_pay">Net Pay Agreement</option>
              <option value="relief_at_source">Relief at Source</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <label htmlFor="pensionContribution" className="sr-only">Pension contribution amount</label>
              <input
                id="pensionContribution"
                type="number"
                min="0"
                max={inputs.pensionType === 'percentage' ? 100 : 100000}
                value={inputs.pensionContribution}
                onChange={(e) => handleChange('pensionContribution', parseFloat(e.target.value) || 0)}
                className="w-full border border-zinc-800 rounded-lg pl-3 pr-8 py-1.5 text-sm font-bold text-white font-mono bg-zinc-900 focus:border-emerald-500 focus:outline-none"
              />
              <span className="absolute right-2.5 top-2 text-xs text-zinc-400 font-mono font-bold">
                {inputs.pensionType === 'percentage' ? '%' : '£'}
              </span>
            </div>
            <label htmlFor="pensionType" className="sr-only">Pension contribution unit</label>
            <select
              id="pensionType"
              value={inputs.pensionType}
              onChange={(e) => handleChange('pensionType', e.target.value as 'percentage' | 'fixed')}
              className="border border-zinc-800 rounded-lg px-2 py-1.5 text-xs text-zinc-300 bg-zinc-900 font-medium focus:border-emerald-500 focus:outline-none"
            >
              <option value="percentage">% of salary</option>
              <option value="fixed">£ / year</option>
            </select>
          </div>
        </div>

        {/* Student Loan Plan */}
        <div className="border border-zinc-800 rounded-xl p-3.5 space-y-2 bg-zinc-950/60">
          <label htmlFor="studentLoanPlan" className="text-xs font-bold text-white flex items-center gap-1.5">
            <GraduationCap className="w-3.5 h-3.5 text-blue-400" />
            Student Loan Deductions
          </label>
          <select
            id="studentLoanPlan"
            value={inputs.studentLoanPlan}
            onChange={(e) => handleChange('studentLoanPlan', e.target.value as StudentLoanPlan)}
            className="w-full border border-zinc-800 rounded-lg px-2.5 py-2 text-xs text-zinc-200 font-medium bg-zinc-900 focus:border-emerald-500 focus:outline-none"
          >
            <option value="none">No Student Loan</option>
            <option value="plan1">Plan 1 (Threshold £24,990 - 9%)</option>
            <option value="plan2">Plan 2 (Threshold £27,295 - 9%)</option>
            <option value="plan4">Plan 4 Scotland (Threshold £31,395 - 9%)</option>
            <option value="plan5">Plan 5 (Threshold £25,000 - 9%)</option>
            <option value="postgrad">Postgraduate Loan (Threshold £21,000 - 6%)</option>
            <option value="plan1_postgrad">Plan 1 + Postgraduate Loan</option>
            <option value="plan2_postgrad">Plan 2 + Postgraduate Loan</option>
            <option value="plan4_postgrad">Plan 4 + Postgraduate Loan</option>
            <option value="plan5_postgrad">Plan 5 + Postgraduate Loan</option>
          </select>
        </div>
      </div>

      {/* Toggle Advanced Options Section */}
      <div className="border-t border-zinc-800 pt-4">
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="flex items-center justify-between w-full py-2.5 px-3 text-xs font-bold text-zinc-200 bg-zinc-950 hover:bg-zinc-800 rounded-xl transition-colors border border-zinc-800"
        >
          <span className="flex items-center gap-2">
            <Sliders className="w-4 h-4 text-emerald-400" />
            Advanced Tax Settings (Bonus, Overtime, BIK, NI Category)
          </span>
          {showAdvanced ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
        </button>

        {showAdvanced && (
          <div className="mt-4 p-4 bg-zinc-950 rounded-xl border border-zinc-800 space-y-4 text-xs">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* Tax Year */}
              <div>
                <label htmlFor="taxYear" className="block text-zinc-300 font-medium mb-1">Tax Year</label>
                <select
                  id="taxYear"
                  value={inputs.taxYear}
                  onChange={(e) => handleChange('taxYear', e.target.value as TaxYear)}
                  className="w-full border border-zinc-800 rounded-lg p-2 bg-zinc-900 text-white font-medium focus:border-emerald-500 focus:outline-none"
                >
                  <option value="2025/26">2025 / 2026 (Current)</option>
                  <option value="2024/25">2024 / 2025</option>
                  <option value="2023/24">2023 / 2024</option>
                </select>
              </div>

              {/* NI Category */}
              <div>
                <label htmlFor="niCategory" className="block text-zinc-300 font-medium mb-1">National Insurance Category</label>
                <select
                  id="niCategory"
                  value={inputs.niCategory}
                  onChange={(e) => handleChange('niCategory', e.target.value as NICategory)}
                  className="w-full border border-zinc-800 rounded-lg p-2 bg-zinc-900 text-white font-medium focus:border-emerald-500 focus:outline-none"
                >
                  <option value="A">Category A (Standard Employee)</option>
                  <option value="B">Category B (Married Women Reduced)</option>
                  <option value="C">Category C (State Pension Age - 0% NI)</option>
                  <option value="H">Category H (Apprentice under 25)</option>
                  <option value="M">Category M (Under 21)</option>
                  <option value="V">Category V (Armed Forces Veteran)</option>
                </select>
              </div>

              {/* Annual Bonus */}
              <div>
                <label htmlFor="bonus" className="block text-zinc-300 font-medium mb-1">Annual Bonus (£)</label>
                <input
                  id="bonus"
                  type="number"
                  min="0"
                  value={inputs.bonus}
                  onChange={(e) => handleChange('bonus', parseFloat(e.target.value) || 0)}
                  placeholder="0"
                  className="w-full border border-zinc-800 rounded-lg p-2 bg-zinc-900 font-mono font-bold text-white focus:border-emerald-500 focus:outline-none"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* Overtime Hours / Month */}
              <div>
                <label htmlFor="overtimeHoursPerMonth" className="block text-zinc-300 font-medium mb-1">Overtime (Hours / Month)</label>
                <input
                  id="overtimeHoursPerMonth"
                  type="number"
                  min="0"
                  value={inputs.overtimeHoursPerMonth}
                  onChange={(e) => handleChange('overtimeHoursPerMonth', parseFloat(e.target.value) || 0)}
                  className="w-full border border-zinc-800 rounded-lg p-2 bg-zinc-900 font-mono font-bold text-white focus:border-emerald-500 focus:outline-none"
                />
              </div>

              {/* Overtime Rate */}
              <div>
                <label htmlFor="overtimeHourlyRate" className="block text-zinc-300 font-medium mb-1">Overtime Rate (£ / Hour)</label>
                <input
                  id="overtimeHourlyRate"
                  type="number"
                  min="0"
                  value={inputs.overtimeHourlyRate}
                  onChange={(e) => handleChange('overtimeHourlyRate', parseFloat(e.target.value) || 0)}
                  className="w-full border border-zinc-800 rounded-lg p-2 bg-zinc-900 font-mono font-bold text-white focus:border-emerald-500 focus:outline-none"
                />
              </div>

              {/* Benefit in Kind */}
              <div>
                <label htmlFor="benefitInKind" className="block text-zinc-300 font-medium mb-1">Benefit in Kind / BIK (£/yr)</label>
                <input
                  id="benefitInKind"
                  type="number"
                  min="0"
                  value={inputs.benefitInKind}
                  onChange={(e) => handleChange('benefitInKind', parseFloat(e.target.value) || 0)}
                  placeholder="e.g. 3000 company car"
                  className="w-full border border-zinc-800 rounded-lg p-2 bg-zinc-900 font-mono font-bold text-white focus:border-emerald-500 focus:outline-none"
                />
              </div>
            </div>

            {/* Checkboxes for Allowances */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-zinc-800">
              <label className="flex items-center gap-2 cursor-pointer text-zinc-300">
                <input
                  type="checkbox"
                  checked={inputs.isBlind}
                  onChange={(e) => handleChange('isBlind', e.target.checked)}
                  className="rounded border-zinc-800 bg-zinc-900 text-emerald-500 focus:ring-emerald-500 w-4 h-4"
                />
                <span>Blind Person's Allowance (+£3,070 tax-free)</span>
              </label>

              <div className="flex items-center gap-2 text-zinc-300">
                <label htmlFor="marriageAllowance">Marriage Allowance:</label>
                <select
                  id="marriageAllowance"
                  value={inputs.marriageAllowance}
                  onChange={(e) => handleChange('marriageAllowance', e.target.value as 'none' | 'transfer_out' | 'receive')}
                  className="border border-zinc-800 rounded px-2 py-1 bg-zinc-900 text-white focus:border-emerald-500 focus:outline-none"
                >
                  <option value="none">Standard / None</option>
                  <option value="receive">Receiving (+£1,260 allowance)</option>
                  <option value="transfer_out">Transferring (-£1,260)</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

